import type { WorldSummary } from '../network/protocol';
import {
  hasContinuableSingleplayerSave,
  loadLastSessionHint,
  loadPlayerSave,
  type LastSessionHint,
} from '../core/playerSave';

export type MenuChoice =
  | { mode: 'singleplayer'; displayName: string; continueSave: boolean }
  | { mode: 'host-new'; displayName: string; worldName: string }
  | { mode: 'host-existing'; displayName: string; code: string }
  | { mode: 'join'; displayName: string; code: string };

type MenuView = 'root' | 'online' | 'host' | 'join';

const NAME_KEY = 'minelunky.displayName';

export interface MainMenuOptions {
  /** Opens the shared controls settings overlay (pre-game). */
  onOpenControls?: () => void;
}

/**
 * Full-screen mode select: singleplayer or online host/join.
 */
export class MainMenu {
  private readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private view: MenuView = 'root';
  private worlds: WorldSummary[] = [];
  private status = '';
  private resolveChoice: ((choice: MenuChoice) => void) | null = null;
  private loadWorlds: (() => Promise<WorldSummary[]>) | null = null;
  private readonly onOpenControls: (() => void) | null;

  constructor(parent: HTMLElement, options: MainMenuOptions = {}) {
    this.onOpenControls = options.onOpenControls ?? null;
    this.root = document.createElement('div');
    this.root.id = 'main-menu';
    this.root.innerHTML = `
      <div class="menu-backdrop"></div>
      <div class="menu-panel" role="dialog" aria-label="Minelunky menu"></div>
    `;
    parent.appendChild(this.root);
    this.panel = this.root.querySelector('.menu-panel') as HTMLElement;
    this.injectStyles();
    this.render();
  }

  async choose(loadWorlds: () => Promise<WorldSummary[]>): Promise<MenuChoice> {
    this.loadWorlds = loadWorlds;
    this.root.style.display = 'grid';
    this.view = 'root';
    this.status = '';
    this.render();
    return new Promise((resolve) => {
      this.resolveChoice = resolve;
    });
  }

  setStatus(message: string): void {
    this.status = message;
    this.render();
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }

  private finish(choice: MenuChoice): void {
    const resolve = this.resolveChoice;
    this.resolveChoice = null;
    if (resolve) resolve(choice);
  }

  private async refreshWorlds(): Promise<void> {
    if (!this.loadWorlds) return;
    this.status = 'Loading saved worlds…';
    this.render();
    try {
      this.worlds = await this.loadWorlds();
      this.status = this.worlds.length ? '' : 'No saved worlds yet — create one.';
    } catch (err) {
      this.worlds = [];
      this.status =
        err instanceof Error ? err.message : 'Could not reach multiplayer server.';
    }
    this.render();
  }

  private render(): void {
    const name = localStorage.getItem(NAME_KEY) || 'Miner';
    const playerId = localStorage.getItem('minelunky.playerId') || 'local';
    const save = loadPlayerSave(playerId, name);
    const canContinue = hasContinuableSingleplayerSave(save);
    const continueStage = save?.singleplayer?.stage ?? 0;
    const lastSession = loadLastSessionHint();
    const statusHtml = this.status
      ? `<p class="menu-status">${escapeHtml(this.status)}</p>`
      : '';

    if (this.view === 'root') {
      const continueBtn = canContinue
        ? `<button type="button" data-action="continue">Continue — stage ${continueStage}</button>`
        : '';
      const lastHint = lastSessionHintHtml(lastSession);
      this.panel.innerHTML = `
        <h1>Minelunky</h1>
        <p class="menu-sub">Mine deeper. Die often. Bank what you can.</p>
        <label class="menu-field">Display name
          <input id="menu-name" type="text" maxlength="24" value="${escapeAttr(name)}" />
        </label>
        <div class="menu-actions">
          ${continueBtn}
          <button type="button" data-action="single">${
            canContinue ? 'New singleplayer run' : 'Singleplayer'
          }</button>
          <button type="button" data-action="online" class="secondary">Online</button>
          <button type="button" data-action="controls" class="ghost">Settings</button>
        </div>
        ${lastHint}
        ${statusHtml}
      `;
    } else if (this.view === 'online') {
      this.panel.innerHTML = `
        <h1>Online</h1>
        <p class="menu-sub">Host a saved world or join with a 4-digit code.</p>
        <div class="menu-actions">
          <button type="button" data-action="host">Host a world</button>
          <button type="button" data-action="join-view" class="secondary">Join with code</button>
          <button type="button" data-action="back" class="ghost">Back</button>
        </div>
        ${statusHtml}
      `;
    } else if (this.view === 'host') {
      const list =
        this.worlds.length === 0
          ? `<p class="menu-muted">No worlds yet.</p>`
          : `<ul class="world-list">${this.worlds
              .map(
                (w) => `
              <li>
                <button type="button" data-host-code="${escapeAttr(w.code)}" class="world-row">
                  <span class="world-name">${escapeHtml(w.name)}</span>
                  <span class="world-meta">Code ${escapeHtml(w.code)} · Stage ${w.stage}${
                    w.hosting ? ' · hosting' : ''
                  }</span>
                </button>
              </li>`,
              )
              .join('')}</ul>`;
      this.panel.innerHTML = `
        <h1>Host a world</h1>
        <p class="menu-sub">Create a new cave or resume one you saved earlier.</p>
        <label class="menu-field">New world name
          <input id="menu-world-name" type="text" maxlength="40" placeholder="Deep Run" />
        </label>
        <div class="menu-actions">
          <button type="button" data-action="host-new">Create & host</button>
          <button type="button" data-action="refresh" class="secondary">Refresh list</button>
          <button type="button" data-action="back-online" class="ghost">Back</button>
        </div>
        <h2 class="menu-h2">Saved worlds</h2>
        ${list}
        ${statusHtml}
      `;
    } else {
      this.panel.innerHTML = `
        <h1>Join</h1>
        <p class="menu-sub">Enter the host’s 4-digit code.</p>
        <label class="menu-field">Room code
          <input id="menu-code" type="text" inputmode="numeric" maxlength="4" placeholder="1234" value="${escapeAttr(
            lastJoinCode(lastSession),
          )}" />
        </label>
        <div class="menu-actions">
          <button type="button" data-action="join">Join game</button>
          <button type="button" data-action="back-online" class="ghost">Back</button>
        </div>
        ${statusHtml}
      `;
    }

    this.bind();
  }

  private bind(): void {
    const nameInput = this.panel.querySelector('#menu-name') as HTMLInputElement | null;
    const saveName = (): string => {
      const value = (nameInput?.value || localStorage.getItem(NAME_KEY) || 'Miner').trim() || 'Miner';
      localStorage.setItem(NAME_KEY, value);
      return value;
    };

    this.panel.querySelectorAll('[data-action]').forEach((el) => {
      el.addEventListener('click', () => {
        const action = (el as HTMLElement).dataset.action;
        if (action === 'continue') {
          this.finish({ mode: 'singleplayer', displayName: saveName(), continueSave: true });
        } else if (action === 'single') {
          this.finish({ mode: 'singleplayer', displayName: saveName(), continueSave: false });
        } else if (action === 'controls') {
          this.onOpenControls?.();
        } else if (action === 'online') {
          this.view = 'online';
          this.status = '';
          this.render();
        } else if (action === 'host') {
          this.view = 'host';
          void this.refreshWorlds();
        } else if (action === 'join-view') {
          this.view = 'join';
          this.status = '';
          this.render();
        } else if (action === 'back') {
          this.view = 'root';
          this.status = '';
          this.render();
        } else if (action === 'back-online') {
          this.view = 'online';
          this.status = '';
          this.render();
        } else if (action === 'refresh') {
          void this.refreshWorlds();
        } else if (action === 'host-new') {
          const worldNameInput = this.panel.querySelector(
            '#menu-world-name',
          ) as HTMLInputElement | null;
          const worldName = (worldNameInput?.value || 'Deep Run').trim() || 'Deep Run';
          this.finish({
            mode: 'host-new',
            displayName: saveName(),
            worldName,
          });
        } else if (action === 'join') {
          const codeInput = this.panel.querySelector('#menu-code') as HTMLInputElement | null;
          const code = (codeInput?.value || '').replace(/\D/g, '').slice(0, 4);
          if (code.length !== 4) {
            this.status = 'Enter a 4-digit code.';
            this.render();
            return;
          }
          this.finish({ mode: 'join', displayName: saveName(), code });
        }
      });
    });

    this.panel.querySelectorAll('[data-host-code]').forEach((el) => {
      el.addEventListener('click', () => {
        const code = (el as HTMLElement).dataset.hostCode || '';
        this.finish({ mode: 'host-existing', displayName: saveName(), code });
      });
    });

    const codeInput = this.panel.querySelector('#menu-code') as HTMLInputElement | null;
    codeInput?.addEventListener('input', () => {
      codeInput.value = codeInput.value.replace(/\D/g, '').slice(0, 4);
    });
  }

  private injectStyles(): void {
    if (document.getElementById('main-menu-styles')) return;
    const style = document.createElement('style');
    style.id = 'main-menu-styles';
    style.textContent = `
      #main-menu {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        z-index: 40;
        pointer-events: auto;
      }
      #main-menu .menu-backdrop {
        position: absolute;
        inset: 0;
        background:
          radial-gradient(ellipse at 30% 20%, rgba(70, 110, 140, 0.35), transparent 55%),
          radial-gradient(ellipse at 70% 80%, rgba(40, 70, 50, 0.4), transparent 50%),
          linear-gradient(160deg, #121820 0%, #1a2430 45%, #0e141c 100%);
      }
      #main-menu .menu-panel {
        position: relative;
        width: min(420px, calc(100vw - 32px));
        padding: 28px 26px 24px;
        border: 1px solid rgba(232, 238, 245, 0.14);
        background: rgba(14, 20, 28, 0.88);
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(8px);
      }
      #main-menu h1 {
        margin: 0 0 6px;
        font-size: 28px;
        letter-spacing: 0.04em;
        font-weight: 700;
      }
      #main-menu .menu-h2 {
        margin: 18px 0 8px;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        opacity: 0.7;
        font-weight: 600;
      }
      #main-menu .menu-sub {
        margin: 0 0 18px;
        opacity: 0.75;
        font-size: 13px;
        line-height: 1.4;
      }
      #main-menu .menu-field {
        display: grid;
        gap: 6px;
        margin-bottom: 14px;
        font-size: 12px;
        opacity: 0.9;
      }
      #main-menu input {
        width: 100%;
        box-sizing: border-box;
        padding: 10px 12px;
        border: 1px solid rgba(232, 238, 245, 0.2);
        background: rgba(0, 0, 0, 0.35);
        color: #e8eef5;
        font: inherit;
        font-size: 15px;
      }
      #main-menu input:focus {
        outline: 1px solid rgba(94, 200, 255, 0.55);
      }
      #main-menu .menu-actions {
        display: grid;
        gap: 8px;
      }
      #main-menu button {
        appearance: none;
        border: 1px solid rgba(232, 238, 245, 0.22);
        background: #2a4a62;
        color: #e8eef5;
        font: inherit;
        font-size: 14px;
        padding: 11px 14px;
        cursor: pointer;
        text-align: left;
      }
      #main-menu button.secondary {
        background: rgba(42, 74, 98, 0.45);
      }
      #main-menu button.ghost {
        background: transparent;
        border-color: transparent;
        opacity: 0.75;
      }
      #main-menu button:hover {
        filter: brightness(1.08);
      }
      #main-menu .world-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 6px;
        max-height: 220px;
        overflow: auto;
      }
      #main-menu .world-row {
        width: 100%;
        display: grid;
        gap: 2px;
        background: rgba(0, 0, 0, 0.28);
      }
      #main-menu .world-name {
        font-size: 14px;
      }
      #main-menu .world-meta {
        font-size: 11px;
        opacity: 0.65;
      }
      #main-menu .menu-status {
        margin: 14px 0 0;
        font-size: 12px;
        color: #ffb4a2;
      }
      #main-menu .menu-muted {
        margin: 0;
        font-size: 12px;
        opacity: 0.55;
      }
      #main-menu .menu-hint {
        margin: 12px 0 0;
        font-size: 12px;
        opacity: 0.65;
        line-height: 1.35;
      }
    `;
    document.head.appendChild(style);
  }
}

function lastSessionHintHtml(hint: LastSessionHint | null): string {
  if (!hint) return '';
  if (hint.mode === 'singleplayer') {
    return `<p class="menu-hint">Last session: singleplayer (progress is saved on this device).</p>`;
  }
  if (hint.mode === 'host') {
    return `<p class="menu-hint">Last online host code: ${escapeHtml(hint.code)} — reopen Host to resume that world.</p>`;
  }
  return `<p class="menu-hint">Last joined code: ${escapeHtml(hint.code)}.</p>`;
}

function lastJoinCode(hint: LastSessionHint | null): string {
  if (hint && (hint.mode === 'join' || hint.mode === 'host')) return hint.code;
  return '';
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, '&#39;');
}
